import {useContext, useEffect, useState} from 'react';
import {MessageDescriptor} from 'react-intl';
import {
  MenuItem,
  SoundSetting,
  Visibility,
  TriggerParams,
  WidgetAPI,
  WidgetNotificationsAPI,
} from './api';
import {WidgetContext} from './WidgetProvider';

export const useWidgetControls = () => {
  const widget = useContext(WidgetContext);

  return {
    open: widget.open,
    close: widget.close,
    setOnClose: widget.setOnClose,
    minimize: widget.minimize,
    toggle: widget.toggle,
    reset: widget.reset,
    setPreferredSize: widget.setPreferredSize,
    requestViewMode: widget.requestViewMode,
    setMinimizedHeight: widget.setMinimizedHeight,
    requestAnalyticsEventId: widget.requestAnalyticsEventId,
  };
};

export const useWidgetVisibility = () => {
  const widget = useContext(WidgetContext);

  const [visibility, setVisibility] = useState<Visibility>(widget.visibility);

  useEffect(() => {
    const sub = widget.visibilityObservable().subscribe(setVisibility);
    return () => sub.unsubscribe();
  }, [widget]);

  return {
    visibility,
  };
};

export const useWidgetTriggerParams = () => {
  const widget = useContext(WidgetContext);

  const [triggerParams, setTriggerParams] = useState<TriggerParams | undefined>(
    widget.triggerParams() ?? undefined,
  );

  useEffect(() => {
    const sub = widget.triggerParamsObservable().subscribe((tp) => {
      setTriggerParams(tp);
    });
    return () => sub.unsubscribe();
  }, [widget]);

  return {
    triggerParams,
  };
};

export const useWidgetNotifications = (): WidgetNotificationsAPI => {
  const {notifications} = useContext(WidgetContext);
  const [soundSetting, setSoundSetting] = useState<SoundSetting>(
    notifications.soundSetting,
  );

  useEffect(() => {
    const sub = notifications
      .soundSettingObservable()
      .subscribe(setSoundSetting);
    return () => sub.unsubscribe();
  }, [notifications]);
  return {
    setUnseenMessagesCount: notifications.setUnseenMessagesCount,
    clearUnseenMessagesCount: notifications.clearUnseenMessagesCount,
    playSound: notifications.playSound,
    setSoundSetting: notifications.setSoundSetting,
    soundSetting,
    soundSettingObservable: notifications.soundSettingObservable,
  };
};

export const useWidgetTitle = () => {
  const widget = useContext(WidgetContext);

  const [title, setTitle] = useState<MessageDescriptor | React.ReactNode>(
    widget.header.title,
  );

  useEffect(() => {
    const sub = widget.header.titleObservable().subscribe(setTitle);
    return () => sub.unsubscribe();
  }, [widget]);

  return {
    title,
    setTitle: widget.header.setTitle,
  };
};

export const useWidgetConversationId = () => {
  const widget = useContext(WidgetContext);

  const [conversationId, setConversationId] = useState<string | undefined>(
    widget.header.conversationId,
  );

  useEffect(() => {
    const sub = widget.header
      .conversationIdObservable()
      .subscribe(setConversationId);
    return () => sub.unsubscribe();
  }, [widget]);

  return {
    conversationId,
    setConversationId: widget.header.setConversationId,
  };
};

export const useWidgetRoutingMode = () => {
  const widget = useContext(WidgetContext);

  const [routingMode, setRoutingMode] = useState<string | undefined>(
    widget.header.routingMode,
  );

  useEffect(() => {
    const sub = widget.header.routingModeObservable().subscribe(setRoutingMode);
    return () => sub.unsubscribe();
  }, [widget]);

  return {
    routingMode,
    setRoutingMode: widget.header.setRoutingMode,
  };
};

export const useWidgetMenu = () => {
  const widget = useContext(WidgetContext);

  const [menuItems, setMenuItems] = useState<MenuItem[]>(widget.menu.items);

  useEffect(() => {
    const sub = widget.menu.itemsObservable().subscribe(setMenuItems);
    return () => {
      sub.unsubscribe();
    };
  }, [widget]);

  return {
    menuItems,
    setMenuItems: widget.menu.setMenuItems,
  };
};

export const useWidget = (): WidgetAPI => {
  return useContext(WidgetContext);
};
